import { useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Mail, Send } from "lucide-react";
import api, { formatApiError } from "@/lib/api";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/contact", form);
      toast.success(data.message || "Nachricht gesendet!");
      setForm({ name: "", email: "", subject: "", message: "" });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Senden fehlgeschlagen.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="kontakt" className="scroll-mt-24 py-20 lg:py-28">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-5 gap-12">
        <div className="lg:col-span-2">
          <span className="text-xs uppercase tracking-widest font-semibold text-[#C87D55]">Kontakt</span>
          <h2 className="mt-3 font-display text-3xl sm:text-4xl font-bold tracking-tight text-[#1C2D24]">
            Schreiben Sie uns
          </h2>
          <p className="mt-4 text-lg text-[#4A5D52] leading-relaxed">
            Haben Sie Fragen zu unseren Inhalten oder Ihrem Zugang? Wir helfen gern weiter.
          </p>
          <a
            href="mailto:foodbone@gmx.de"
            className="mt-6 inline-flex items-center gap-3 bg-[#E2EBE1] text-[#1E3F20] rounded-2xl px-5 py-4 font-medium hover:bg-[#d5e2d3] transition-colors"
          >
            <Mail className="w-5 h-5" /> foodbone@gmx.de
          </a>
        </div>

        <form onSubmit={submit} className="lg:col-span-3 bg-white rounded-3xl ring-1 ring-[#E2DDD3] p-6 sm:p-8 space-y-5">
          <div className="grid sm:grid-cols-2 gap-5">
            <div>
              <Label htmlFor="c-name" className="text-[#1C2D24]">Name</Label>
              <Input id="c-name" data-testid="contact-name-input" required value={form.name} onChange={set("name")} className="mt-1.5 bg-[#F9F8F3] border-[#E2DDD3]" placeholder="Ihr Name" />
            </div>
            <div>
              <Label htmlFor="c-email" className="text-[#1C2D24]">E-Mail</Label>
              <Input id="c-email" type="email" data-testid="contact-email-input" required value={form.email} onChange={set("email")} className="mt-1.5 bg-[#F9F8F3] border-[#E2DDD3]" placeholder="ihre@email.de" />
            </div>
          </div>
          <div>
            <Label htmlFor="c-subject" className="text-[#1C2D24]">Betreff</Label>
            <Input id="c-subject" data-testid="contact-subject-input" required value={form.subject} onChange={set("subject")} className="mt-1.5 bg-[#F9F8F3] border-[#E2DDD3]" placeholder="Worum geht es?" />
          </div>
          <div>
            <Label htmlFor="c-message" className="text-[#1C2D24]">Nachricht</Label>
            <Textarea id="c-message" data-testid="contact-message-input" required rows={5} value={form.message} onChange={set("message")} className="mt-1.5 bg-[#F9F8F3] border-[#E2DDD3]" placeholder="Ihre Nachricht..." />
          </div>
          <Button
            type="submit"
            data-testid="contact-submit-button"
            disabled={loading}
            className="w-full bg-[#1E3F20] hover:bg-[#163017] text-white rounded-full h-12 text-base"
          >
            {loading ? "Wird gesendet..." : <><Send className="w-4 h-4 mr-2" /> Nachricht absenden</>}
          </Button>
        </form>
      </div>
    </section>
  );
}
